import {
  FC,
  Ref,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from 'react';
import classNames from 'classnames';

interface State {
  x?: number;
  y?: number;
  maxX?: number;
  maxY?: number;
}

interface Props {
  horizontal?: boolean;
  vertical?: boolean;
  scrollbars?: boolean;
  enableScrollWheel?: boolean;
  state?: State;
  setState?: (State) => any;
}

const getPosition = ({ scrollLeft = 0, scrollTop = 0 }) => ({
  x: scrollLeft,
  y: scrollTop,
});

const getSize = ({
  scrollWidth = 0,
  scrollHeight = 0,
  clientWidth = 0,
  clientHeight = 0,
}) => ({
  maxX: scrollWidth - clientWidth,
  maxY: scrollHeight - clientHeight,
});

const noOp = () => {};

const ScrollArea: FC<Props> = (
  {
    className: classNameProp,
    horizontal = false,
    vertical = true,
    scrollbars = true,
    enableScrollWheel = true,
    state,
    setState = noOp,
    ...props
  },
  forwardedRef: Ref
) => {
  const className = classNames(
    'scroll-area',
    {
      'scroll-area__horizontal': horizontal,
      'scroll-area__vertical': vertical,
      'scroll-area__without-scrollbars': !scrollbars,
    },
    classNameProp
  );

  const ref = useRef({});

  const { x = 0, y = 0 } = state || getPosition(ref.current);

  useEffect(() => {
    ref.current.scrollLeft = x;
    ref.current.scrollTop = y;
  }, [x, y]);

  const { maxX, maxY } = getSize(ref.current);

  useEffect(() => {
    setState({
      ...getPosition(ref.current),
      maxX,
      maxY,
    });
  }, [setState, maxX, maxY]);

  useEffect(() => {
    const { current: area } = ref;
    const blockWheel = event => event.preventDefault();

    if (!enableScrollWheel)
      area.addEventListener('wheel', blockWheel, { passive: false });

    return () => area.removeEventListener('wheel', blockWheel);
  }, [enableScrollWheel]);

  const previousPosition = useRef({});

  useEffect(() => {
    previousPosition.current = { ...getPosition(ref.current) };
  });

  const locked = useRef(false);

  useImperativeHandle(forwardedRef, () => ({
    lock: () => {
      locked.current = true;
    },
    unlock: () => {
      locked.current = false;
    },
  }));

  useEffect(() => {
    const { current: area } = ref;

    const onScroll = event => {
      const { target } = event;

      // Locking mechanism to forcibly set the scroll position back to the
      // previous value. Unfortunately it is necessary because clicking the
      // resize handles from react-rnd inside a ScrollArea causes the scroll
      // position to jump.
      if (locked.current) {
        target.scrollLeft = previousPosition.current.x;
        target.scrollTop = previousPosition.current.y;
      } else {
        setState({
          ...getPosition(target),
          ...getSize(target),
        });
      }
    };

    area.addEventListener('scroll', onScroll, { passive: true });
    return () => area.removeEventListener('scroll', onScroll);
  }, [setState]);

  return <div {...props} ref={ref} className={className} />;
};

export default forwardRef(ScrollArea);
